import {
  ClaimMethod,
  type ClaimRequestViewDto,
} from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Label,
  Textarea,
  cn,
  healthAttentionSurfaceClass,
  healthOkSurfaceClass,
} from '@up2stay/up2promo-ui';
import { Lock, Mail, Package } from 'lucide-react';
import { useId, useState } from 'react';

import {
  ClaimCancelButton,
  ClaimResendButton,
} from '@/core/components/products/claim-requests-panel';
import {
  fillClaimTemplate,
  useClaimDict,
  useClaimTimeFormat,
  useNow,
} from '@/core/hooks/use-claim-requests';
import type { ClaimApiError } from '@/core/lib/claim-requests';
import { CLAIM_MANUAL_NOTE_MAX } from '@/core/lib/claim-requests';
import { useDictionary } from '@/core/providers/dictionary-provider';

function useClaimProductDict() {
  const dict = useDictionary();
  return (
    (dict as { claim?: { product?: Record<string, string> } })?.claim
      ?.product ?? {}
  );
}

export function ClaimOutcomeIdle({
  onCheck,
  onScanAnother,
}: {
  onCheck: () => void;
  onScanAnother: () => void;
}) {
  const t = useClaimProductDict();
  return (
    <div className="flex gap-2">
      <Button onClick={onCheck}>{t.check}</Button>
      <Button variant="outline" onClick={onScanAnother}>
        {t.scanAnother}
      </Button>
    </div>
  );
}

export function ClaimOutcomeChecking() {
  const t = useClaimProductDict();
  return <p className="text-sm text-muted-foreground">{t.checking}</p>;
}

export function ClaimOutcomeCantClaim({
  onScanAnother,
}: {
  onScanAnother: () => void;
}) {
  const t = useClaimProductDict();
  return (
    <Card className="border-destructive/50 bg-destructive/5">
      <CardContent className="pt-6">
        <p className="text-sm font-medium text-destructive">
          {t.cantClaimTitle}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t.cantClaimMessage}
        </p>
        <Button variant="outline" className="mt-3" onClick={onScanAnother}>
          {t.scanAnother}
        </Button>
      </CardContent>
    </Card>
  );
}

export function ClaimOutcomeAssignedToOtherUser({
  onScanAnother,
}: {
  onScanAnother: () => void;
}) {
  const t = useClaimProductDict();
  return (
    <Card className="border-destructive/50 bg-destructive/5">
      <CardContent className="pt-6">
        <p className="text-sm font-medium text-destructive">
          {t.assignedToOtherUserTitle}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t.assignedToOtherUserMessage}
        </p>
        <Button variant="outline" className="mt-3" onClick={onScanAnother}>
          {t.scanAnother}
        </Button>
      </CardContent>
    </Card>
  );
}

export function ClaimOutcomeAlreadyYours({
  onGoToProducts,
}: {
  onGoToProducts: () => void;
}) {
  const t = useClaimProductDict();
  return (
    <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
      <CardContent className="pt-6">
        <p className="text-sm font-medium text-green-800 dark:text-green-200">
          {t.alreadyYoursTitle}
        </p>
        <p className="mt-1 text-sm text-green-700 dark:text-green-300">
          {t.alreadyYoursMessage}
        </p>
        <Button variant="outline" className="mt-3" onClick={onGoToProducts}>
          {t.goToProducts}
        </Button>
      </CardContent>
    </Card>
  );
}

export function ClaimOutcomeSendClaimEmail({
  onSubmit,
  isPending,
}: {
  onSubmit: () => void;
  isPending: boolean;
}) {
  const t = useClaimProductDict();
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm font-medium">{t.sendClaimTitle}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t.sendClaimMessage}
        </p>
        <Button className="mt-3" onClick={onSubmit} disabled={isPending}>
          {isPending ? t.submitting : t.submit}
        </Button>
      </CardContent>
    </Card>
  );
}

export function ClaimOutcomeAddViaSetup({
  onGoToSetup,
}: {
  onGoToSetup: () => void;
}) {
  const t = useClaimProductDict();
  return (
    <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
      <CardContent className="pt-6">
        <p className="text-sm font-medium text-green-800 dark:text-green-200">
          {t.addViaSetupTitle}
        </p>
        <p className="mt-1 text-sm text-green-700 dark:text-green-300">
          {t.addViaSetupMessage}
        </p>
        <Button className="mt-3" onClick={onGoToSetup}>
          {t.goToSetup}
        </Button>
      </CardContent>
    </Card>
  );
}

export function ClaimOutcomeError({
  onScanAnother,
}: {
  onScanAnother: () => void;
}) {
  const t = useClaimProductDict();
  return (
    <Button variant="outline" onClick={onScanAnother}>
      {t.scanAnother}
    </Button>
  );
}

/** Inline error card (instead of a toast) for claim actions (DEV-756). */
export function ClaimOutcomeInlineError({
  title,
  message,
}: {
  title?: string;
  message: string;
}) {
  return (
    <div
      role="alert"
      className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm"
    >
      {title ? <p className="font-medium text-destructive">{title}</p> : null}
      <p
        className={cn(
          title ? 'mt-0.5 text-muted-foreground' : 'text-destructive',
        )}
      >
        {message}
      </p>
    </div>
  );
}

/** Member without `products:manage`: the product is claimable, not by them. */
export function ClaimOutcomeNoPermission({
  onScanAnother,
}: {
  onScanAnother: () => void;
}) {
  const t = useClaimDict().product;
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 pt-6">
        <div className="flex items-start gap-3">
          <Lock
            className="mt-0.5 size-5 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <div className="text-sm">
            <p className="font-medium">{t.noPermissionTitle}</p>
            <p className="mt-1 text-muted-foreground">
              {t.noPermissionMessage}
            </p>
          </div>
        </div>
        <Button variant="outline" className="w-fit" onClick={onScanAnother}>
          {t.scanAnother}
        </Button>
      </CardContent>
    </Card>
  );
}

/** Masked address, when it was sent and how long the link is valid. */
function ClaimEmailFacts({ request }: { request: ClaimRequestViewDto }) {
  const t = useClaimDict().product;
  const time = useClaimTimeFormat();
  const now = useNow(30_000);
  return (
    <dl className="grid gap-1 rounded-lg bg-muted/50 px-3 py-2.5 text-sm">
      {request.maskedEmail ? (
        <div className="flex items-center gap-2">
          <Mail className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <dt className="sr-only">{t.pendingEmailLabel}</dt>
          <dd className="font-medium">{request.maskedEmail}</dd>
        </div>
      ) : null}
      <dd className="text-xs text-muted-foreground">
        {fillClaimTemplate(t.pendingSent, {
          ago: time.ago(request.lastSentAt ?? request.createdAt, now),
        })}
        {' · '}
        <time dateTime={request.expiresAt}>
          {fillClaimTemplate(t.pendingValidUntil, {
            date: time.dateTime(request.expiresAt),
          })}
        </time>
      </dd>
    </dl>
  );
}

/**
 * The verification mail just went out (after `send_claim_email`). The
 * request lives on server-side, so leaving the page is fine.
 */
export function ClaimOutcomeEmailSent({
  request,
  onGoToProducts,
  onError,
}: {
  request?: ClaimRequestViewDto;
  onGoToProducts: () => void;
  onError: (error: ClaimApiError) => void;
}) {
  const t = useClaimDict().product;
  return (
    <Card
      className={healthOkSurfaceClass}
      data-testid="claim-outcome-email-sent"
    >
      <CardContent className="flex flex-col gap-3 pt-6">
        <div className="flex items-start gap-3">
          <Package
            className="mt-0.5 size-5 shrink-0 text-[color:var(--ok-fg)]"
            aria-hidden
          />
          <div className="text-sm">
            <p className="font-medium">{t.successTitle}</p>
            <p className="mt-1 text-muted-foreground">
              {request?.maskedEmail
                ? fillClaimTemplate(t.successMessage, {
                    email: request.maskedEmail,
                  })
                : t.successMessageNoEmail}
            </p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">{t.leaveHint}</p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={onGoToProducts}>{t.goToProducts}</Button>
          {request ? (
            <ClaimResendButton request={request} onError={onError} />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/** `claim_pending` (EMAIL): this workspace already asked for the product. */
export function ClaimOutcomePendingEmail({
  request,
  onGoToProducts,
  onError,
  onCancelled,
}: {
  request?: ClaimRequestViewDto;
  onGoToProducts: () => void;
  onError: (error: ClaimApiError) => void;
  onCancelled: () => void;
}) {
  const t = useClaimDict().product;
  const pendingT = useClaimDict().pending;
  return (
    <Card
      className={healthAttentionSurfaceClass}
      data-testid="claim-outcome-pending"
    >
      <CardContent className="flex flex-col gap-3 pt-6">
        <div className="text-sm">
          <p className="font-medium">{t.pendingTitle}</p>
          <p className="mt-1 text-muted-foreground">{t.pendingMessage}</p>
        </div>
        {request ? (
          <>
            <ClaimEmailFacts request={request} />
            <div className="flex flex-wrap gap-2">
              <ClaimResendButton request={request} onError={onError} />
              <ClaimCancelButton
                request={request}
                variant="ghost"
                label={t.pendingCancel}
                onDone={onCancelled}
              />
            </div>
          </>
        ) : null}
        <Button
          variant="primary"
          mode="link"
          underline="solid"
          className="w-fit text-sm"
          onClick={onGoToProducts}
        >
          {pendingT.viewAll}
        </Button>
      </CardContent>
    </Card>
  );
}

/** `claim_pending` (MANUAL): waiting for the up2promo team. */
export function ClaimOutcomePendingManual({
  request,
  onGoToProducts,
  onCancelled,
}: {
  request?: ClaimRequestViewDto;
  onGoToProducts: () => void;
  onCancelled: () => void;
}) {
  const claim = useClaimDict();
  const t = claim.product;
  const time = useClaimTimeFormat();
  return (
    <Card data-testid="claim-outcome-pending-manual">
      <CardContent className="flex flex-col gap-3 pt-6">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="text-sm font-medium">{t.pendingManualTitle}</p>
          <Badge variant="info">{claim.status.PENDING_MANUAL}</Badge>
        </div>
        {request ? (
          <p className="text-sm text-muted-foreground">
            {fillClaimTemplate(t.pendingManualMessage, {
              date: time.date(request.createdAt),
            })}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button onClick={onGoToProducts}>{t.goToProducts}</Button>
          {request && request.method === ClaimMethod.MANUAL ? (
            <ClaimCancelButton
              request={request}
              variant="ghost"
              label={t.pendingManualWithdraw}
              onDone={onCancelled}
            />
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * `claim_manual`: no setup e-mail on file, so the up2promo team reviews the
 * request. Replaces the former "contact our team" dead end.
 */
export function ClaimOutcomeManualForm({
  hash,
  isPending,
  onSubmit,
  onContactTeam,
  contactLoading,
}: {
  hash: string;
  isPending: boolean;
  onSubmit: (note: string) => void;
  onContactTeam: () => void;
  contactLoading: boolean;
}) {
  const t = useClaimDict().manual;
  const [note, setNote] = useState('');
  const noteId = useId();
  const counterId = useId();
  const hintId = useId();
  return (
    <Card data-testid="claim-outcome-manual-form">
      <CardContent className="pt-6">
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit(note);
          }}
        >
          <div className="text-sm">
            <p className="font-medium">{t.title}</p>
            <p className="mt-1 text-muted-foreground">{t.message}</p>
          </div>
          <div className="flex items-center justify-between gap-2 rounded-lg bg-muted/50 px-3 py-2 text-sm">
            <span className="text-muted-foreground">{t.hashLabel}</span>
            <span className="font-mono">{hash}</span>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={noteId}>{t.noteLabel}</Label>
            <Textarea
              id={noteId}
              value={note}
              maxLength={CLAIM_MANUAL_NOTE_MAX}
              rows={3}
              placeholder={t.notePlaceholder}
              aria-describedby={`${hintId} ${counterId}`}
              onChange={(event) => setNote(event.target.value)}
            />
            <div className="flex justify-between gap-2 text-xs text-muted-foreground">
              <span id={hintId}>{t.noteHint}</span>
              <span id={counterId}>
                {fillClaimTemplate(t.noteCounter, {
                  count: note.length,
                  max: CLAIM_MANUAL_NOTE_MAX,
                })}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="primary" disabled={isPending}>
              {isPending ? t.submitting : t.submit}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={contactLoading}
              onClick={onContactTeam}
            >
              {t.contactTeam}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{t.afterHint}</p>
        </form>
      </CardContent>
    </Card>
  );
}
